import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Collapse,
    IconButton,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from "@mui/material";
import MyToastShow from "@/components/Layout/MyToast";
import { EstadosInformeCell } from "@/components/Tablas/TablaCarpetaV2/components/TableRowComponents/InformeEstadosCell";
import TableCarpetaEstados from "@/components/Tablas/TablaCarpetaV2/components/TableRowComponents/TableCarpetaEstados";
import { ViewAs } from "@/components/Tablas/TablaCarpetaV2/logics/useTablaCarritoV2";
import UsuarioCellData from "@/components/Tablas/TableBase/UsuarioCell/UsuarioCell";
import dayjs from "dayjs";
import { useState } from "react";
import { ICarrito, IInforme, velocidades } from "@/redux/models";
import { formatToCurrencyARS } from "@/utils/allUtils";
import { obtenerNombreInforme } from "@/utils/informeUtils";
import { usePagarCarritosMutation } from "../../redux/api/informeApi";
import { useAppSelector } from "../../redux/store";

type ModalLiquidarInformesProps = {
    open: boolean;
    onCancel: () => void;
    carpetas: ICarrito[];
    onCarpetasPagadas: (carpetas: ICarrito[]) => void;
    informes: Record<number, IInforme[]>;
};

const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "95vw",
    width: 1000,
    boxShadow: 24,
    p: 4,
    borderRadius: 3,
};

export default function ModalPagarLMCarpetas({
    carpetas,
    informes,
    open,
    onCancel,
    onCarpetasPagadas,
}: ModalLiquidarInformesProps) {
    const darkMode = useAppSelector((state: any) => state.theme.darkTheme);
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));
    const boxWidth = isMobile ? "100%" : "70rem";

    const [pagarCarritos] = usePagarCarritosMutation();
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState<number[]>([]);

    const carpetasAPagar = carpetas.filter(carpeta => carpeta.liquidacionMensual && !carpeta.pagado);

    const totalPago = carpetasAPagar.reduce((sum, carpeta) => sum + (carpeta.montoTotal || 0), 0);


    const handlePagar = () => {
        setLoading(true);
        pagarCarritos(carpetasAPagar.map((c) => c.idCarrito))
            .unwrap()
            .then(() => {
                MyToastShow({
                    message: "Las carpetas se pagaron correctamente",
                    type: "success",
                });
                onCarpetasPagadas(carpetasAPagar);
            })
            .finally(() => {
                setLoading(false);
                onCancel();
            });
    };

    const toggleRow = (id: number) => {
        setExpandedRows((prev) =>
            prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
        );
    };

    return (
        <Modal
            open={open}
            onClose={onCancel}
            aria-labelledby="pagar-informes"
            sx={{ width: "100%" }}
        >
            <Box
                sx={{
                    ...style,
                    width: boxWidth,
                    bgcolor: darkMode ? "#1e1e1e" : "#fff",
                    maxHeight: "85vh",
                    overflow: "auto",
                }}
                component={Paper}
                elevation={4}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                        Pagar Carpetas con Liquidación Mensual (LM)
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "medium" }}>
                        Total: <strong>{formatToCurrencyARS(totalPago)}</strong>
                    </Typography>
                </Box>

                {carpetasAPagar.length > 0 ? (
                    <>
                        <Typography variant="body1" sx={{ mb: 3, color: darkMode ? "#ccc" : "#555" }}>
                            Está a punto de pagar {carpetasAPagar.length} carpeta(s).
                        </Typography>

                        <TableContainer sx={{ maxHeight: "50vh", borderRadius: 2, bgcolor: darkMode ? "#2a2a2a" : "#f9f9f9" }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold", width: 50 }} />
                                        <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold" }}>ID</TableCell>
                                        <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold" }}>Nombre</TableCell>
                                        <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold" }}>Fecha</TableCell>
                                        <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold" }}>Monto</TableCell>
                                        <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold" }}>Usuario</TableCell>
                                        <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold" }}>Estado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {carpetasAPagar.map((carpeta) => (
                                        <>
                                            <TableRow key={`carrito-${carpeta.idCarrito}`} hover>
                                                <TableCell>
                                                    <IconButton onClick={() => toggleRow(carpeta.idCarrito)}>
                                                        {expandedRows.includes(carpeta.idCarrito) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>{carpeta.idCarrito}</TableCell>
                                                <TableCell>{carpeta.nombreCarrito}</TableCell>
                                                <TableCell>{dayjs(carpeta.fechaDeCreacionCarrito).format("DD/MM/YYYY HH:mm")}</TableCell>
                                                <TableCell>{formatToCurrencyARS(carpeta.montoTotal)}</TableCell>
                                                <TableCell>
                                                    <UsuarioCellData usuario={carpeta.usuario} />
                                                </TableCell>
                                                <TableCell>
                                                    <TableCarpetaEstados
                                                        carpeta={carpeta}
                                                        hayRechazados={false}
                                                        setModal={() => { }}
                                                        viewAs={ViewAs.ADMIN}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={7} sx={{ py: 0, bgcolor: darkMode ? "#252525" : "#f5f5f5" }}>
                                                    <Collapse in={expandedRows.includes(carpeta.idCarrito)}>
                                                        <Box sx={{ p: 2 }}>
                                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                                                                Informes en la carpeta:
                                                            </Typography>
                                                            {informes[carpeta.idCarrito]?.length > 0 ? (
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                                                                                Entrada
                                                                            </TableCell>
                                                                            <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                                                                                Fecha
                                                                            </TableCell>
                                                                            <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                                                                                Informe
                                                                            </TableCell>
                                                                            <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                                                                                Velocidad
                                                                            </TableCell>
                                                                            <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                                                                                Precio
                                                                            </TableCell>
                                                                            <TableCell sx={{ bgcolor: darkMode ? "#333" : "#e0e0e0", fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                                                                                Estado
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {informes[carpeta.idCarrito].map((informe) => (
                                                                            <TableRow key={`informe-${informe.id}`}>
                                                                                <TableCell component="th" scope="row">
                                                                                    {informe.entrada}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {dayjs(informe.fechaDeCreacion).format('DD/MM/YYYY')}
                                                                                </TableCell>
                                                                                <TableCell component="th" scope="row">
                                                                                    {obtenerNombreInforme(informe, false, { velocidad: true })}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {velocidades[informe.velocidad]}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {formatToCurrencyARS(informe.precio)}
                                                                                </TableCell>
                                                                                <TableCell size="small">
                                                                                    <EstadosInformeCell informe={informe} />
                                                                                </TableCell>
                                                                                {/* <TableCell>
                                                                                    <InformeAction viewAs={context.viewAs} setModal={context.setModal} modal={context.modal} informe={informe} modificarInforme={context.modificarInforme} />
                                                                                </TableCell> */}
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            ) : (
                                                                <Typography variant="body2">No hay informes en esta carpeta.</Typography>
                                                            )}
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                            <LoadingButton
                                loading={loading}
                                variant="contained"
                                color="error"
                                onClick={handlePagar}
                                sx={{ mr: 1, px: 3 }}
                            >
                                Pagar
                            </LoadingButton>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={onCancel}
                                sx={{ px: 3 }}
                            >
                                Cancelar
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Typography variant="body1" sx={{ mb: 2, color: darkMode ? "#ccc" : "#555" }}>
                        No hay carpetas para pagar.
                    </Typography>
                )}
            </Box>
        </Modal>
    );
}